/**
 * SWOTView.jsx - SWOT Analysis Visualization
 *
 * Multiple view modes for sentiment/agency analysis:
 * 1. SWOT Grid - Classic 2x2 quadrant view
 * 2. Pie Chart - Sentiment distribution donut
 * 3. Divergent Bar - Horizontal bars with center axis
 * 4. Scatter Plot - 2D positioning with exact values
 * 5. List View - Sortable table with all details
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Container, ViewToggle, ViewButton, GridContainer, Quadrant, QuadrantLabel, QuadrantCount,
  ItemsContainer, ItemBubble, CenterLines, AxisLabels, XAxisLabel, YAxisLabel, PieContainer,
  PieWrapper, PieLegend, OptimismScoreCard, OptimismEmoji, OptimismLabel, OptimismValue,
  OptimismDescription, OptimismBar, BarContainer, BarItem, BarLabel, BarTrack, BarCenterLine,
  BarFill, BarValue, ScatterContainer, ScatterPoint, ScatterAxisLabel, ScatterGridLine,
  ListContainer, ListTable, ListHeader, ListRow, QuadrantBadge, SentimentIndicator, ModalOverlay,
  ModalContent, ModalCloseButton, ModalTitle, ModalQuadrant, ModalReasoning, ModalSection,
  ModalSectionTitle, ExampleList, ScoreBar, MetaInfo,
} from './SWOTView.styles';
import {
  MoodContainer, MoodOverallCard, MoodOverallScore, MoodOverallEmoji, MoodOverallValue,
  MoodOverallLabel, MoodOverallDescription, MoodScenarioGrid, MoodScenarioCard, MoodScenarioHeader,
  MoodScenarioName, MoodScenarioEmoji, MoodScenarioScore, MoodScenarioValue, MoodScenarioPercent,
  MoodScenarioBar, MoodScenarioMeta, MoodQuadrantBreakdown, MoodQuadrantChip, VoteMeterContainer,
  VoteMeterLabel, VoteMeterBar, VoteMeterFillDown, VoteMeterFillUp, VoteMeterCenter, VoteCounts,
  VoteCount, ScenarioModalContent, ScenarioModalHeader, ScenarioModalEmoji, ScenarioModalInfo,
  ScenarioModalTitle, ScenarioModalScore, ScenarioModalValue, ScenarioModalLabel,
  ScenarioQuadrantSection, ScenarioQuadrantHeader, ScenarioQuadrantIcon, ScenarioQuadrantTitle,
  ScenarioQuadrantCount, ScenarioItemList, ScenarioItem, ScenarioItemTitle, ScenarioItemMeta,
  ScenarioEmptyMessage, LegendItem, LegendColor, LegendLabel, LegendValue,
} from './SWOTView.moodStyles';
import {
  X, TrendingUp, TrendingDown, Shield, AlertTriangle,
  Grid3X3, PieChart, BarChart3, ScatterChart, List,
  ArrowUpDown, ChevronDown, ChevronUp, Smile,
  ThumbsUp, ThumbsDown
} from 'lucide-react';
import API_ENDPOINTS from '../../config/api';
import SocketService from '../../../../../packages/shared/socket';

// Quadrant colors
const QUADRANT_COLORS = {
  strength: '#4CAF50',   // Green
  weakness: '#F44336',   // Red
  opportunity: '#2196F3', // Blue
  threat: '#FF9800'       // Orange
};

const QUADRANT_LABELS = {
  strength: { fi: 'VAHVUUDET', en: 'STRENGTHS', icon: TrendingUp },
  weakness: { fi: 'HEIKKOUDET', en: 'WEAKNESSES', icon: TrendingDown },
  opportunity: { fi: 'MAHDOLLISUUDET', en: 'OPPORTUNITIES', icon: Shield },
  threat: { fi: 'UHAT', en: 'THREATS', icon: AlertTriangle }
};

const VIEW_MODES = [
  { id: 'grid', label: 'SWOT', icon: Grid3X3 },
  { id: 'pie', label: 'Piirakka', icon: PieChart },
  { id: 'mood', label: 'Tunnelma', icon: Smile },
  { id: 'bar', label: 'Palkit', icon: BarChart3 },
  { id: 'scatter', label: 'Hajonta', icon: ScatterChart },
  { id: 'list', label: 'Lista', icon: List }
];

// ============ Styled Components ============


// ============ COMPONENT ============

function SWOTView({ swotItems = [], stats = {}, loading = false, onRefresh, viewMode, onViewModeChange, instanceId }) {
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [sortField, setSortField] = useState('memberCount');
  const [sortDir, setSortDir] = useState('desc');

  // Live vote data (updated via socket + HTTP polling fallback)
  const [liveVotes, setLiveVotes] = useState(null);

  // Fetch votes via HTTP
  const fetchVotes = useCallback(async () => {
    if (!instanceId) return;
    try {
      const response = await fetch(API_ENDPOINTS.RAG_VOTES(instanceId));
      if (response.ok) {
        const data = await response.json();
        console.log('[SWOT] Vote data received:', data);
        setLiveVotes(data);
      } else {
        console.error('[SWOT] Vote fetch failed:', response.status);
      }
    } catch (error) {
      console.error('[SWOT] Failed to fetch live votes:', error);
    }
  }, [instanceId]);

  // Socket + polling for live vote updates
  useEffect(() => {
    if (!instanceId) return;

    // Initial fetch
    fetchVotes();

    // Join instance room for real-time updates
    const socket = SocketService.socket;
    if (socket && socket.connected) {
      socket.emit('join-instance', { instanceId, deviceType: 'display' });

      // Listen for vote updates
      const handleVoteUpdate = (data) => {
        console.log('[SWOT] Received vote update:', data);
        // Refetch to get accurate totals
        fetchVotes();
      };

      socket.on('node-vote-updated', handleVoteUpdate);

      return () => {
        socket.off('node-vote-updated', handleVoteUpdate);
      };
    }

    // Fallback: Poll every 30 seconds if socket not available
    const interval = setInterval(fetchVotes, 30000);
    return () => clearInterval(interval);
  }, [instanceId, fetchVotes]);

  // Group items by quadrant
  const groupedItems = useMemo(() => ({
    strength: swotItems.filter(i => i.quadrant === 'strength'),
    weakness: swotItems.filter(i => i.quadrant === 'weakness'),
    opportunity: swotItems.filter(i => i.quadrant === 'opportunity'),
    threat: swotItems.filter(i => i.quadrant === 'threat')
  }), [swotItems]);

  // Sorted items for list view
  const sortedItems = useMemo(() => {
    return [...swotItems].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (typeof aVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [swotItems, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  // ============ RENDER GRID VIEW ============
  const renderGridView = () => {
    const renderQuadrant = (quadrant, position) => {
      const items = groupedItems[quadrant];
      const color = QUADRANT_COLORS[quadrant];
      const label = QUADRANT_LABELS[quadrant];
      const Icon = label.icon;

      return (
        <Quadrant $color={color}>
          <QuadrantLabel $color={color} $position={position}>
            <Icon />
            {label.fi}
          </QuadrantLabel>
          <QuadrantCount $color={color} $position={position}>
            {items.length}
          </QuadrantCount>
          <ItemsContainer>
            {items.map(item => (
              <ItemBubble
                key={item.id}
                $color={color}
                $size={item.memberCount}
                onClick={() => setSelectedItem(item)}
                title={`${item.title} (${item.memberCount} jäsentä)`}
              >
                {item.title}
              </ItemBubble>
            ))}
          </ItemsContainer>
        </Quadrant>
      );
    };

    return (
      <>
        <GridContainer>
          {renderQuadrant('weakness', 'left')}
          {renderQuadrant('strength', 'right')}
          {renderQuadrant('threat', 'left')}
          {renderQuadrant('opportunity', 'right')}
          <CenterLines />
        </GridContainer>
        <AxisLabels>
          <XAxisLabel>
            <span>NEGATIIVINEN</span>
            <span>→</span>
            <span>POSITIIVINEN</span>
          </XAxisLabel>
          <YAxisLabel>
            <span>ULKOINEN</span>
            <span>→</span>
            <span>SISÄINEN</span>
          </YAxisLabel>
        </AxisLabels>
      </>
    );
  };

  // ============ RENDER PIE VIEW ============
  const renderPieView = () => {
    const total = swotItems.length || 1;
    const counts = {
      strength: groupedItems.strength.length,
      weakness: groupedItems.weakness.length,
      opportunity: groupedItems.opportunity.length,
      threat: groupedItems.threat.length
    };

    // Calculate Future Optimism Score
    // 1. Weighted average of sentiment scores (-1 to 1)
    const avgSentiment = swotItems.length > 0
      ? swotItems.reduce((sum, item) => sum + (item.sentiment || 0), 0) / swotItems.length
      : 0;

    // 2. Ratio of positive quadrants (strength + opportunity) vs total
    const positiveCount = counts.strength + counts.opportunity;
    const negativeCount = counts.weakness + counts.threat;
    const positiveRatio = total > 0 ? positiveCount / total : 0.5;

    // 3. Combined score: 60% sentiment average + 40% positive ratio
    // Convert from -1..1 to 0..100 scale
    const sentimentScore = (avgSentiment + 1) / 2 * 100;
    const ratioScore = positiveRatio * 100;
    const optimismScore = Math.round(sentimentScore * 0.6 + ratioScore * 0.4);

    // Determine emoji and description based on score
    const getOptimismData = (score) => {
      if (score >= 75) return {
        emoji: '🌟',
        color: '#4CAF50',
        description: 'Erittäin positiivinen tulevaisuuskuva! Vahvuuksia ja mahdollisuuksia tunnistettu runsaasti.'
      };
      if (score >= 60) return {
        emoji: '😊',
        color: '#8BC34A',
        description: 'Varovaisen optimistinen näkymä. Mahdollisuuksia nähdään enemmän kuin uhkia.'
      };
      if (score >= 45) return {
        emoji: '🤔',
        color: '#FFC107',
        description: 'Tasapainoinen näkemys. Sekä haasteita että mahdollisuuksia tunnistettu.'
      };
      if (score >= 30) return {
        emoji: '😟',
        color: '#FF9800',
        description: 'Huolestuneisuutta ilmassa. Enemmän haasteita kuin mahdollisuuksia.'
      };
      return {
        emoji: '😰',
        color: '#F44336',
        description: 'Kriittinen tilanne. Paljon uhkia ja heikkouksia tunnistettu.'
      };
    };

    const optimismData = getOptimismData(optimismScore);

    // Get vote totals - prefer liveVotes (real-time) over stats
    const totalVotesUp = liveVotes?.totalVotesUp ?? stats?.totalVotesUp ?? swotItems.reduce((sum, item) => sum + (item.votesUp || 0), 0);
    const totalVotesDown = liveVotes?.totalVotesDown ?? stats?.totalVotesDown ?? swotItems.reduce((sum, item) => sum + (item.votesDown || 0), 0);
    const totalVotes = totalVotesUp + totalVotesDown;
    const voteUpPercent = totalVotes > 0 ? (totalVotesUp / totalVotes) * 100 : 50;
    const voteDownPercent = totalVotes > 0 ? (totalVotesDown / totalVotes) * 100 : 50;

    // Calculate pie segments
    let currentAngle = -90; // Start from top
    const segments = Object.entries(counts).map(([quadrant, count]) => {
      const percentage = (count / total) * 100;
      const angle = (count / total) * 360;
      const startAngle = currentAngle;
      currentAngle += angle;
      return { quadrant, count, percentage, startAngle, angle };
    });

    // SVG pie chart
    const createArcPath = (startAngle, angle, innerRadius, outerRadius) => {
      if (angle >= 360) angle = 359.99;
      const startRad = (startAngle * Math.PI) / 180;
      const endRad = ((startAngle + angle) * Math.PI) / 180;
      const x1 = 150 + outerRadius * Math.cos(startRad);
      const y1 = 150 + outerRadius * Math.sin(startRad);
      const x2 = 150 + outerRadius * Math.cos(endRad);
      const y2 = 150 + outerRadius * Math.sin(endRad);
      const x3 = 150 + innerRadius * Math.cos(endRad);
      const y3 = 150 + innerRadius * Math.sin(endRad);
      const x4 = 150 + innerRadius * Math.cos(startRad);
      const y4 = 150 + innerRadius * Math.sin(startRad);
      const largeArc = angle > 180 ? 1 : 0;

      return `M ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4} Z`;
    };

    return (
      <PieContainer>
        {/* Optimism Score Card */}
        <OptimismScoreCard>
          <OptimismEmoji>{optimismData.emoji}</OptimismEmoji>
          <OptimismLabel>Tulevaisuusoptimismi</OptimismLabel>
          <OptimismValue $color={optimismData.color}>{optimismScore}%</OptimismValue>
          <OptimismBar $position={optimismScore} />
          <OptimismDescription>{optimismData.description}</OptimismDescription>
          <div style={{ marginTop: '16px', fontSize: '0.7rem', color: '#666' }}>
            Positiiviset: {positiveCount} | Negatiiviset: {negativeCount}
          </div>

          {/* Vote Meter - always show, centered divergent bar */}
          <VoteMeterContainer style={{ marginTop: '20px' }}>
            <VoteMeterLabel>Osallistujien äänet</VoteMeterLabel>
            <VoteMeterBar>
              <VoteMeterFillDown style={{ width: `${voteDownPercent / 2}%` }} />
              <VoteMeterCenter />
              <VoteMeterFillUp style={{ width: `${voteUpPercent / 2}%` }} />
            </VoteMeterBar>
            <VoteCounts>
              <VoteCount $color="#F44336">
                <ThumbsDown /> {totalVotesDown}
              </VoteCount>
              <VoteCount $color="#4CAF50">
                <ThumbsUp /> {totalVotesUp}
              </VoteCount>
            </VoteCounts>
          </VoteMeterContainer>
        </OptimismScoreCard>

        <PieWrapper>
          <svg width="300" height="300" viewBox="0 0 300 300">
            {segments.map(seg => seg.count > 0 && (
              <path
                key={seg.quadrant}
                d={createArcPath(seg.startAngle, seg.angle, 60, 130)}
                fill={QUADRANT_COLORS[seg.quadrant]}
                opacity="0.8"
                style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                onMouseEnter={(e) => e.target.style.opacity = '1'}
                onMouseLeave={(e) => e.target.style.opacity = '0.8'}
              />
            ))}
            {/* Center text */}
            <text x="150" y="145" textAnchor="middle" fill="#fff" fontSize="28" fontWeight="600">
              {total}
            </text>
            <text x="150" y="165" textAnchor="middle" fill="#888" fontSize="12">
              teemaa
            </text>
          </svg>
        </PieWrapper>

        <PieLegend>
          {Object.entries(QUADRANT_LABELS).map(([quadrant, label]) => (
            <LegendItem key={quadrant}>
              <LegendColor $color={QUADRANT_COLORS[quadrant]} />
              <LegendLabel>{label.fi}</LegendLabel>
              <LegendValue>{counts[quadrant]}</LegendValue>
            </LegendItem>
          ))}
        </PieLegend>
      </PieContainer>
    );
  };

  // ============ RENDER MOOD VIEW ============
  const renderMoodView = () => {
    // Helper function to calculate optimism score
    const calcOptimismScore = (items) => {
      if (items.length === 0) return { score: 50, emoji: '🤔', color: '#FFC107', description: 'Ei dataa' };

      const avgSentiment = items.reduce((sum, item) => sum + (item.sentiment || 0), 0) / items.length;
      const positiveCount = items.filter(i => i.quadrant === 'strength' || i.quadrant === 'opportunity').length;
      const positiveRatio = items.length > 0 ? positiveCount / items.length : 0.5;

      const sentimentScore = (avgSentiment + 1) / 2 * 100;
      const ratioScore = positiveRatio * 100;
      const score = Math.round(sentimentScore * 0.6 + ratioScore * 0.4);

      if (score >= 75) return { score, emoji: '🌟', color: '#4CAF50', description: 'Erittäin positiivinen' };
      if (score >= 60) return { score, emoji: '😊', color: '#8BC34A', description: 'Optimistinen' };
      if (score >= 45) return { score, emoji: '🤔', color: '#FFC107', description: 'Tasapainoinen' };
      if (score >= 30) return { score, emoji: '😟', color: '#FF9800', description: 'Huolestunut' };
      return { score, emoji: '😰', color: '#F44336', description: 'Kriittinen' };
    };

    // Calculate overall score
    const overallData = calcOptimismScore(swotItems);

    // Group items by scenario
    const scenarioGroups = {};
    swotItems.forEach(item => {
      (item.scenarioNames || ['Yleinen']).forEach(scenarioName => {
        if (!scenarioGroups[scenarioName]) {
          scenarioGroups[scenarioName] = [];
        }
        scenarioGroups[scenarioName].push(item);
      });
    });

    // Calculate score for each scenario
    const scenarioScores = Object.entries(scenarioGroups).map(([name, items]) => {
      const data = calcOptimismScore(items);
      const quadrantCounts = {
        strength: items.filter(i => i.quadrant === 'strength').length,
        weakness: items.filter(i => i.quadrant === 'weakness').length,
        opportunity: items.filter(i => i.quadrant === 'opportunity').length,
        threat: items.filter(i => i.quadrant === 'threat').length
      };
      return {
        name,
        ...data,
        itemCount: items.length,
        quadrantCounts,
        items // Include the actual items for the modal
      };
    }).sort((a, b) => b.score - a.score);

    // Get vote totals - prefer liveVotes (real-time) over stats
    const totalVotesUp = liveVotes?.totalVotesUp ?? stats?.totalVotesUp ?? swotItems.reduce((sum, item) => sum + (item.votesUp || 0), 0);
    const totalVotesDown = liveVotes?.totalVotesDown ?? stats?.totalVotesDown ?? swotItems.reduce((sum, item) => sum + (item.votesDown || 0), 0);
    const totalVotes = totalVotesUp + totalVotesDown;
    const voteUpPercent = totalVotes > 0 ? (totalVotesUp / totalVotes) * 100 : 50;
    const voteDownPercent = totalVotes > 0 ? (totalVotesDown / totalVotes) * 100 : 50;

    return (
      <MoodContainer>
        {/* Overall Score Card */}
        <MoodOverallCard>
          <MoodOverallScore>
            <MoodOverallEmoji>{overallData.emoji}</MoodOverallEmoji>
            <MoodOverallValue $color={overallData.color}>{overallData.score}%</MoodOverallValue>
            <MoodOverallLabel>Kokonaisoptimismi</MoodOverallLabel>
          </MoodOverallScore>
          <MoodOverallDescription>
            <strong style={{ color: overallData.color }}>{overallData.description}</strong>
            <br /><br />
            Työpajan osallistujat tunnistavat yhteensä <strong>{swotItems.length}</strong> teemaa,
            joista <strong style={{ color: '#4CAF50' }}>{groupedItems.strength.length + groupedItems.opportunity.length}</strong> on
            positiivisia (vahvuuksia ja mahdollisuuksia) ja <strong style={{ color: '#F44336' }}>{groupedItems.weakness.length + groupedItems.threat.length}</strong> on
            haasteita (heikkouksia ja uhkia).
            {scenarioScores.length > 1 && (
              <>
                <br /><br />
                Optimistisin skenaario on <strong style={{ color: scenarioScores[0].color }}>{scenarioScores[0].name}</strong> ({scenarioScores[0].score}%).
              </>
            )}
          </MoodOverallDescription>

          {/* Vote Meter - centered divergent bar */}
          {totalVotes > 0 && (
            <VoteMeterContainer>
              <VoteMeterLabel>Osallistujien äänet</VoteMeterLabel>
              <VoteMeterBar>
                <VoteMeterFillDown style={{ width: `${voteDownPercent / 2}%` }} />
                <VoteMeterCenter />
                <VoteMeterFillUp style={{ width: `${voteUpPercent / 2}%` }} />
              </VoteMeterBar>
              <VoteCounts>
                <VoteCount $color="#F44336">
                  <ThumbsDown /> {totalVotesDown}
                </VoteCount>
                <VoteCount $color="#4CAF50">
                  <ThumbsUp /> {totalVotesUp}
                </VoteCount>
              </VoteCounts>
            </VoteMeterContainer>
          )}
        </MoodOverallCard>

        {/* Scenario Grid */}
        {scenarioScores.length > 0 && (
          <>
            <div style={{ color: '#888', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Tunnelma skenaarioittain
            </div>
            <MoodScenarioGrid>
              {scenarioScores.map(scenario => (
                <MoodScenarioCard key={scenario.name} onClick={() => setSelectedScenario(scenario)}>
                  <MoodScenarioHeader>
                    <MoodScenarioName title={scenario.name}>{scenario.name}</MoodScenarioName>
                    <MoodScenarioEmoji>{scenario.emoji}</MoodScenarioEmoji>
                  </MoodScenarioHeader>

                  <MoodScenarioScore>
                    <MoodScenarioValue $color={scenario.color}>{scenario.score}</MoodScenarioValue>
                    <MoodScenarioPercent>%</MoodScenarioPercent>
                  </MoodScenarioScore>

                  <MoodScenarioBar $position={scenario.score} />

                  <MoodQuadrantBreakdown>
                    {scenario.quadrantCounts.strength > 0 && (
                      <MoodQuadrantChip $color={QUADRANT_COLORS.strength}>
                        {scenario.quadrantCounts.strength} vahvuus
                      </MoodQuadrantChip>
                    )}
                    {scenario.quadrantCounts.opportunity > 0 && (
                      <MoodQuadrantChip $color={QUADRANT_COLORS.opportunity}>
                        {scenario.quadrantCounts.opportunity} mahd.
                      </MoodQuadrantChip>
                    )}
                    {scenario.quadrantCounts.weakness > 0 && (
                      <MoodQuadrantChip $color={QUADRANT_COLORS.weakness}>
                        {scenario.quadrantCounts.weakness} heikkous
                      </MoodQuadrantChip>
                    )}
                    {scenario.quadrantCounts.threat > 0 && (
                      <MoodQuadrantChip $color={QUADRANT_COLORS.threat}>
                        {scenario.quadrantCounts.threat} uhka
                      </MoodQuadrantChip>
                    )}
                  </MoodQuadrantBreakdown>

                  <MoodScenarioMeta>
                    <span>{scenario.itemCount} teemaa</span>
                    <span style={{ color: scenario.color }}>{scenario.description}</span>
                  </MoodScenarioMeta>
                </MoodScenarioCard>
              ))}
            </MoodScenarioGrid>
          </>
        )}
      </MoodContainer>
    );
  };

  // ============ RENDER BAR VIEW ============
  const renderBarView = () => {
    return (
      <BarContainer>
        <div style={{ marginBottom: '24px', color: '#888', fontSize: '0.8rem' }}>
          Sävy: Negatiivinen ← 0 → Positiivinen
        </div>
        {sortedItems.map(item => {
          const color = QUADRANT_COLORS[item.quadrant];
          const sentiment = item.sentiment || 0;
          const width = Math.abs(sentiment) * 50;
          const left = sentiment >= 0 ? '50%' : `${50 - width}%`;

          return (
            <BarItem key={item.id} onClick={() => setSelectedItem(item)}>
              <BarLabel title={item.title}>{item.title}</BarLabel>
              <BarTrack>
                <BarCenterLine />
                <BarFill
                  $color={color}
                  style={{ left, width: `${width}%` }}
                />
              </BarTrack>
              <BarValue $color={color}>
                {sentiment > 0 ? '+' : ''}{(sentiment * 100).toFixed(0)}%
              </BarValue>
            </BarItem>
          );
        })}
      </BarContainer>
    );
  };

  // ============ RENDER SCATTER VIEW ============
  const renderScatterView = () => {
    return (
      <ScatterContainer>
        {/* Grid lines */}
        <ScatterGridLine style={{ left: '50%', top: 0, bottom: 0, width: '1px' }} />
        <ScatterGridLine style={{ top: '50%', left: 0, right: 0, height: '1px' }} />
        <ScatterGridLine style={{ left: '25%', top: 0, bottom: 0, width: '1px', opacity: 0.5 }} />
        <ScatterGridLine style={{ left: '75%', top: 0, bottom: 0, width: '1px', opacity: 0.5 }} />
        <ScatterGridLine style={{ top: '25%', left: 0, right: 0, height: '1px', opacity: 0.5 }} />
        <ScatterGridLine style={{ top: '75%', left: 0, right: 0, height: '1px', opacity: 0.5 }} />

        {/* Axis labels */}
        <ScatterAxisLabel style={{ bottom: '8px', left: '50%', transform: 'translateX(-50%)' }}>
          SÄVY (Negatiivinen → Positiivinen)
        </ScatterAxisLabel>
        <ScatterAxisLabel style={{ left: '8px', top: '50%', transform: 'translateY(-50%) rotate(-90deg)', transformOrigin: 'left center' }}>
          TOIMIJUUS (Ulkoinen → Sisäinen)
        </ScatterAxisLabel>

        {/* Quadrant labels */}
        <ScatterAxisLabel style={{ top: '20px', left: '20px', color: QUADRANT_COLORS.weakness }}>Heikkoudet</ScatterAxisLabel>
        <ScatterAxisLabel style={{ top: '20px', right: '20px', color: QUADRANT_COLORS.strength }}>Vahvuudet</ScatterAxisLabel>
        <ScatterAxisLabel style={{ bottom: '30px', left: '20px', color: QUADRANT_COLORS.threat }}>Uhat</ScatterAxisLabel>
        <ScatterAxisLabel style={{ bottom: '30px', right: '20px', color: QUADRANT_COLORS.opportunity }}>Mahdollisuudet</ScatterAxisLabel>

        {/* Data points */}
        {swotItems.map(item => {
          const x = ((item.sentiment + 1) / 2) * 100; // -1..1 → 0..100
          const y = 100 - ((item.agency + 1) / 2) * 100; // Invert Y (top = internal)

          return (
            <ScatterPoint
              key={item.id}
              $color={QUADRANT_COLORS[item.quadrant]}
              $size={item.memberCount}
              style={{ left: `${x}%`, top: `${y}%` }}
              onClick={() => setSelectedItem(item)}
              title={item.title}
            >
              {item.memberCount}
            </ScatterPoint>
          );
        })}
      </ScatterContainer>
    );
  };

  // ============ RENDER LIST VIEW ============
  const renderListView = () => {
    const SortIcon = sortDir === 'asc' ? ChevronUp : ChevronDown;

    return (
      <ListContainer>
        <ListTable>
          <thead>
            <tr>
              <ListHeader onClick={() => handleSort('title')}>
                Teema {sortField === 'title' && <SortIcon />}
              </ListHeader>
              <ListHeader onClick={() => handleSort('quadrant')}>
                Luokka {sortField === 'quadrant' && <SortIcon />}
              </ListHeader>
              <ListHeader onClick={() => handleSort('sentiment')}>
                Sävy {sortField === 'sentiment' && <SortIcon />}
              </ListHeader>
              <ListHeader onClick={() => handleSort('agency')}>
                Toimijuus {sortField === 'agency' && <SortIcon />}
              </ListHeader>
              <ListHeader onClick={() => handleSort('memberCount')}>
                Jäseniä {sortField === 'memberCount' && <SortIcon />}
              </ListHeader>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map(item => (
              <ListRow key={item.id} onClick={() => setSelectedItem(item)}>
                <td style={{ color: '#fff', fontWeight: 500 }}>{item.title}</td>
                <td>
                  <QuadrantBadge $color={QUADRANT_COLORS[item.quadrant]}>
                    {QUADRANT_LABELS[item.quadrant].fi}
                  </QuadrantBadge>
                </td>
                <td>
                  <SentimentIndicator>
                    <div className="bar">
                      <div
                        className="fill"
                        style={{
                          left: '50%',
                          width: `${Math.abs(item.sentiment) * 50}%`,
                          marginLeft: item.sentiment < 0 ? `-${Math.abs(item.sentiment) * 50}%` : 0,
                          background: item.sentiment >= 0 ? '#4CAF50' : '#F44336'
                        }}
                      />
                    </div>
                    <span style={{ color: item.sentiment >= 0 ? '#4CAF50' : '#F44336' }}>
                      {item.sentiment > 0 ? '+' : ''}{(item.sentiment * 100).toFixed(0)}%
                    </span>
                  </SentimentIndicator>
                </td>
                <td>
                  <SentimentIndicator>
                    <div className="bar">
                      <div
                        className="fill"
                        style={{
                          left: '50%',
                          width: `${Math.abs(item.agency) * 50}%`,
                          marginLeft: item.agency < 0 ? `-${Math.abs(item.agency) * 50}%` : 0,
                          background: item.agency >= 0 ? '#2196F3' : '#FF9800'
                        }}
                      />
                    </div>
                    <span style={{ color: item.agency >= 0 ? '#2196F3' : '#FF9800' }}>
                      {item.agency > 0 ? '+' : ''}{(item.agency * 100).toFixed(0)}%
                    </span>
                  </SentimentIndicator>
                </td>
                <td>{item.memberCount}</td>
              </ListRow>
            ))}
          </tbody>
        </ListTable>
      </ListContainer>
    );
  };

  // ============ RENDER SCENARIO DETAIL MODAL ============
  const renderScenarioModal = () => {
    if (!selectedScenario) return null;

    // Group scenario items by quadrant
    const scenarioItemsByQuadrant = {
      strength: selectedScenario.items.filter(i => i.quadrant === 'strength'),
      opportunity: selectedScenario.items.filter(i => i.quadrant === 'opportunity'),
      weakness: selectedScenario.items.filter(i => i.quadrant === 'weakness'),
      threat: selectedScenario.items.filter(i => i.quadrant === 'threat')
    };

    const quadrantOrder = ['strength', 'opportunity', 'weakness', 'threat'];

    return (
      <ModalOverlay
        $visible={true}
        onClick={() => setSelectedScenario(null)}
      >
        <ScenarioModalContent
          $color={selectedScenario.color}
          onClick={e => e.stopPropagation()}
        >
          <ModalCloseButton
            $color={selectedScenario.color}
            onClick={() => setSelectedScenario(null)}
          >
            <X size={18} />
          </ModalCloseButton>

          <ScenarioModalHeader>
            <ScenarioModalEmoji>{selectedScenario.emoji}</ScenarioModalEmoji>
            <ScenarioModalInfo>
              <ScenarioModalTitle>{selectedScenario.name}</ScenarioModalTitle>
              <ScenarioModalScore>
                <ScenarioModalValue $color={selectedScenario.color}>
                  {selectedScenario.score}%
                </ScenarioModalValue>
                <ScenarioModalLabel>
                  {selectedScenario.description} - {selectedScenario.itemCount} teemaa
                </ScenarioModalLabel>
              </ScenarioModalScore>
            </ScenarioModalInfo>
          </ScenarioModalHeader>

          {quadrantOrder.map(quadrant => {
            const items = scenarioItemsByQuadrant[quadrant];
            if (items.length === 0) return null;

            const Icon = QUADRANT_LABELS[quadrant].icon;
            const color = QUADRANT_COLORS[quadrant];

            return (
              <ScenarioQuadrantSection key={quadrant}>
                <ScenarioQuadrantHeader $color={color}>
                  <ScenarioQuadrantIcon $color={color}>
                    <Icon />
                  </ScenarioQuadrantIcon>
                  <ScenarioQuadrantTitle $color={color}>
                    {QUADRANT_LABELS[quadrant].fi}
                  </ScenarioQuadrantTitle>
                  <ScenarioQuadrantCount>
                    {items.length} {items.length === 1 ? 'teema' : 'teemaa'}
                  </ScenarioQuadrantCount>
                </ScenarioQuadrantHeader>

                <ScenarioItemList>
                  {items.map(item => (
                    <ScenarioItem
                      key={item.id}
                      $color={color}
                      onClick={() => {
                        setSelectedScenario(null);
                        setSelectedItem(item);
                      }}
                    >
                      <ScenarioItemTitle>{item.title}</ScenarioItemTitle>
                      <ScenarioItemMeta>
                        <span>Sävy: {(item.sentiment * 100).toFixed(0)}%</span>
                        <span>{item.memberCount} jäsentä</span>
                        {item.reasoning && <span>{item.reasoning}</span>}
                      </ScenarioItemMeta>
                    </ScenarioItem>
                  ))}
                </ScenarioItemList>
              </ScenarioQuadrantSection>
            );
          })}

          {selectedScenario.items.length === 0 && (
            <ScenarioEmptyMessage>
              Ei teemoja tässä skenaariossa.
            </ScenarioEmptyMessage>
          )}
        </ScenarioModalContent>
      </ModalOverlay>
    );
  };

  // ============ RENDER MODAL ============
  const renderModal = () => (
    <ModalOverlay
      $visible={!!selectedItem}
      onClick={() => setSelectedItem(null)}
    >
      {selectedItem && (
        <ModalContent
          $color={QUADRANT_COLORS[selectedItem.quadrant]}
          onClick={e => e.stopPropagation()}
        >
          <ModalCloseButton
            $color={QUADRANT_COLORS[selectedItem.quadrant]}
            onClick={() => setSelectedItem(null)}
          >
            <X size={18} />
          </ModalCloseButton>

          <ModalTitle $color={QUADRANT_COLORS[selectedItem.quadrant]}>
            {selectedItem.title}
          </ModalTitle>

          <ModalQuadrant $color={QUADRANT_COLORS[selectedItem.quadrant]}>
            {QUADRANT_LABELS[selectedItem.quadrant].fi}
          </ModalQuadrant>

          {selectedItem.reasoning && (
            <ModalReasoning>{selectedItem.reasoning}</ModalReasoning>
          )}

          <ModalSection>
            <ModalSectionTitle>Analyysi</ModalSectionTitle>
            <ScoreBar>
              <div className="label">Sävy</div>
              <div className="bar">
                <div
                  className="fill"
                  style={{
                    left: '50%',
                    width: `${Math.abs(selectedItem.sentiment) * 50}%`,
                    marginLeft: selectedItem.sentiment < 0 ? `-${Math.abs(selectedItem.sentiment) * 50}%` : 0,
                    background: selectedItem.sentiment >= 0 ? '#4CAF50' : '#F44336'
                  }}
                />
              </div>
              <div className="value">
                {selectedItem.sentiment > 0 ? '+' : ''}{(selectedItem.sentiment * 100).toFixed(0)}%
              </div>
            </ScoreBar>
            <ScoreBar>
              <div className="label">Toimijuus</div>
              <div className="bar">
                <div
                  className="fill"
                  style={{
                    left: '50%',
                    width: `${Math.abs(selectedItem.agency) * 50}%`,
                    marginLeft: selectedItem.agency < 0 ? `-${Math.abs(selectedItem.agency) * 50}%` : 0,
                    background: selectedItem.agency >= 0 ? '#2196F3' : '#FF9800'
                  }}
                />
              </div>
              <div className="value">
                {selectedItem.agency > 0 ? '+' : ''}{(selectedItem.agency * 100).toFixed(0)}%
              </div>
            </ScoreBar>
          </ModalSection>

          {selectedItem.exampleTexts?.length > 0 && (
            <ModalSection>
              <ModalSectionTitle>Esimerkkejä</ModalSectionTitle>
              <ExampleList>
                {selectedItem.exampleTexts.map((text, i) => (
                  <li key={i}>{text}</li>
                ))}
              </ExampleList>
            </ModalSection>
          )}

          {selectedItem.scenarioNames?.length > 0 && (
            <ModalSection>
              <ModalSectionTitle>Skenaariot</ModalSectionTitle>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {selectedItem.scenarioNames.map(name => (
                  <span
                    key={name}
                    style={{
                      background: '#222',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '0.8rem',
                      color: '#ccc'
                    }}
                  >
                    {name}
                  </span>
                ))}
              </div>
            </ModalSection>
          )}

          <MetaInfo>
            <div>Jäseniä: <span>{selectedItem.memberCount}</span></div>
            <div>Osallistujia: <span>{selectedItem.participantCount}</span></div>
            {selectedItem.isCrossScenario && (
              <div style={{ color: '#4CAF50' }}>Moniskenaarioinen</div>
            )}
          </MetaInfo>
        </ModalContent>
      )}
    </ModalOverlay>
  );

  // ============ MAIN RENDER ============
  return (
    <Container>
      {viewMode === 'grid' && renderGridView()}
      {viewMode === 'pie' && renderPieView()}
      {viewMode === 'mood' && renderMoodView()}
      {viewMode === 'bar' && renderBarView()}
      {viewMode === 'scatter' && renderScatterView()}
      {viewMode === 'list' && renderListView()}
      {renderModal()}
      {renderScenarioModal()}
    </Container>
  );
}

// Export view toggle component separately for use in parent
export function ViewModeToggle({ viewMode, onViewModeChange }) {
  return (
    <ViewToggle>
      {VIEW_MODES.map(mode => (
        <ViewButton
          key={mode.id}
          $active={viewMode === mode.id}
          onClick={() => onViewModeChange(mode.id)}
          title={mode.label}
        >
          <mode.icon />
          {mode.label}
        </ViewButton>
      ))}
    </ViewToggle>
  );
}

export default SWOTView;
